
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Helmet } from 'react-helmet';
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardFooter } from '@/components/ui/card';

interface Provider {
  id: string;
  name: string;
  logo_url?: string;
  description?: string;
}

const AllProvidersPage = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setIsLoading(true);
        // First try to fetch from database
        const { data, error } = await supabase
          .from('providers')
          .select('*')
          .order('name');
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          setProviders(data);
        } else {
          // Use fallback providers if no data is returned
          setProviders([
            { id: '1', name: 'CBB', description: 'Mobilabonnementer fra CBB' },
            { id: '2', name: 'Eesy', description: 'Mobilabonnementer fra Eesy' },
            { id: '3', name: 'Flexii', description: 'Mobilabonnementer fra Flexii' },
            { id: '4', name: 'Oister', description: 'Mobilabonnementer fra Oister' },
            { id: '5', name: 'Telenor', description: 'Mobilabonnementer fra Telenor' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        // Use fallback providers if there's an error
        setProviders([
          { id: '1', name: 'CBB', description: 'Mobilabonnementer fra CBB' },
          { id: '2', name: 'Eesy', description: 'Mobilabonnementer fra Eesy' },
          { id: '3', name: 'Flexii', description: 'Mobilabonnementer fra Flexii' },
          { id: '4', name: 'Oister', description: 'Mobilabonnementer fra Oister' },
          { id: '5', name: 'Telenor', description: 'Mobilabonnementer fra Telenor' },
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProviders();
  }, []);

  // Generate slug from provider name
  const getProviderSlug = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '-');
  };
  
  const canonicalUrl = 'https://abonemt.dk/mobiludbydere';
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Alle Mobiludbydere i Danmark | Abonemt</title>
        <meta name="description" content="Sammenlign mobilabonnementer fra alle danske mobiludbydere. Find den bedste pris og dækning til dine behov." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 md:px-6 pt-20 pb-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold my-8 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
            Alle Mobiludbydere
          </h1>
          
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="h-40 bg-muted flex items-center justify-center">
                    <Skeleton className="h-16 w-32" />
                  </div>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-full mb-1" />
                    <Skeleton className="h-3 w-4/5" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <>
              {providers.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">
                  Ingen udbydere fundet. Prøv igen senere.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {providers.map((provider) => (
                    <Link
                      key={provider.id}
                      to={`/mobiludbyder/${getProviderSlug(provider.name)}`}
                      className="block group"
                    >
                      <Card className="h-full transition-all hover:shadow-md">
                        <div className="h-40 bg-muted flex items-center justify-center p-6">
                          {provider.logo_url ? (
                            <img 
                              src={provider.logo_url} 
                              alt={`${provider.name} logo`} 
                              className="max-h-full max-w-full object-contain"
                            />
                          ) : (
                            <span className="text-xl font-semibold text-primary">{provider.name}</span>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h2 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                            {provider.name}
                          </h2>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {provider.description || `Se alle abonnementer fra ${provider.name}`}
                          </p>
                        </CardContent>
                        <CardFooter className="p-4 pt-0 text-sm text-primary">
                          Se abonnementer →
                        </CardFooter>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
          
          <div className="prose prose-lg mx-auto mt-16">
            <h2>Find det rette mobilabonnement hos en dansk udbyder</h2>
            <p>
              Der findes mange forskellige mobiludbydere i Danmark, hver med deres egne fordele og ulemper.
              Her på Abonemt kan du nemt sammenligne de forskellige udbydere og deres abonnementer,
              så du kan finde det bedste tilbud til dine behov.
            </p>
            <p>
              Uanset om du er på udkig efter det billigste abonnement, den bedste dækning eller det højeste
              dataforbrug, så kan du finde det her. Brug vores sammenligningsværktøj til at finde det perfekte
              mobilabonnement til dig.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllProvidersPage;
